import type {ICpRouteLocationNamedRaw} from '~/utils/services/screens';

interface IPath {
  [pathId: string]: {
    id: string | null;
    image: string | null;
    title: string;
    url: string;
  };
}
/** based on Cyberpuerta\cp\lib\models\Link */
interface ILink {
  id: any;
  title: string;
  url: string;
  image?: string | null;
}
interface IRelatedLink {
  image: string;
  link: string;
  links: IRelatedLink[];
  title: string;
  type: 'heading' | string;
}
export interface ILinkGroup {
  relatedLinks: Array<IRelatedLink>;
}
export interface ICpBreadcrumb {
  id: string | null;
  image: string | null;
  title: string;
  url: string;
}
export interface ICpImg {
  alt: string;
  src: string;
}
export interface ICatMan {
  hasSubCategories: boolean;
  id: string;
  image: string | null;
  link: string;
  parentId: string | null;
  path: IPath | [] | null;
  subCategories: ICatMan[] | null;
  title: string;
  linkGroups?: Array<ILinkGroup>;
}
export interface ICpArticle {
  allReviewsLink: string;
  badges: Object[];
  brand: ICatMan;
  /** same as price, price with iva */
  brutePrice: number;
  bullets: string | string[];
  promoConditions: any[];
  relevance: number;
  category: ICatMan;
  dne: string | null;
  ean: string | null;
  eol: boolean;
  /** tuple of type [int min, int max] */
  etaRange: number[];
  /** unix timestamp. */
  expires: number | null;
  /** timestamp */
  expiresMain: number | null;
  /** y<string, string>  first buyable variant, irrelevant for cyberpuerta {anid: "", aid: "", price: "", fprice: ""} */
  firstBuyable: any;
  /** with format like $0.00 */
  formattedPrice: string;
  /** formatted t-price like $0.00 */
  formattedTPrice: string;
  freeShipping: boolean;
  hasDiscount: boolean;
  id: string;
  isBestPrice: boolean;
  /* should be dynamic and per-user! */
  isCompare?: boolean;
  isDailyOffer: boolean;
  isEsd: boolean;
  isNew: boolean;
  isOffer: boolean;
  isSaldoKickBack: boolean;
  isHardware?: boolean;
  /** seo link, for the main category */
  link: string;
  listAttributes: Object[];
  /** ??? */
  notBuyable: boolean;
  /** assoc. offer object. */
  offer: {
    date: string | boolean;
    percent: string | boolean;
    absolute: string | boolean;
    fabsolute: string;
    year: string | boolean;
    month: string | boolean;
    day: string | boolean;
  };
  /** oxid of parent article, only relevant for AB */
  parentId: string;
  /** pc configurator attributes, from $oxArticle::cppc_attributes() */
  pcAttributes: any;
  pcMessage: string;
  /** pc configurator special */
  pcSpecial: any;
  /** url for main picture */
  picture: string;
  pictures: string[];
  /** final price */
  price: number | string;
  oldPrice?: number | string;
  questionsCount: number | string;
  questionsLink: string;
  rating: Record<`star${'1' | '2' | '3' | '4' | '5'}`, number> & {
    global: number | string;
    total: number | string;
    parent_article_id?: string;
  };
  seoLinks: Object[];
  /** shipping cost, regardless of free shipping or not. */
  shipping: number;
  shippingOriginCountry: string;
  /** oxartnum in the database */
  sku: string;
  /** amount of stock available to sell */
  stock: number;
  /** ??? */
  stockStatus: number | string;
  /** final price without discount. strikeout in website to emphasize discount. */
  tPrice: number;
  /** only the iva part of t-price */
  tPriceVat: number;
  /** display title of the product */
  title: string;
  /** f_cparticle_type */
  type: string;
  unlimitedSupply: boolean;
  /** how many variants this product has (relevant for Abasteo) */
  varCount: number;
  /** price of the cheapest variant (relevant for Abasteo) */
  varMinPrice: number;
  /** only the iva part of the cheapest variant (relevant for Abasteo) */
  varMinPricePat: number;
  /** name of the variant that makes it different from other variants (bodega in Abasteo) */
  varSelect: string;
  /** total stock of the variants (relevant for Abasteo) */
  varStock: number;
  variantName: string;
  /** k[]  careful, only load with variants when strictly necessary */
  variants: ILink[];
  /** only the iva part of the price */
  vat: number;
  warehouseId: string | null;
  links: any;
  pics360: any[];
  bundleIsGift: boolean;
  // New key to hide add to cart button on PDP
  hideAddToCartButton: boolean;
  writeReviewLink?: string;
  warranty?: IArticleWarrantySupplier;
  isCountryVersionMex?: boolean;
  nationalVersion?: string | null;
  isImported?: boolean | null;
}

export interface IArticleBox {
  id: string;
  link: string;
  oldPrice: string;
  picture: string;
  price: string;
  showStarts: boolean;
  sku: string;
  starsWidth: number;
  stock: number;
  title: string;
}
export interface IEtaInfo {
  cpEta: [number, number];
  eta: [number, number];
  etaDates: [number, number];
  extraEta: [number, number];
  messages: Array<{ident: string; lang: string; type: string}>;
  onlyPostOffice: boolean;
  possibleDates: number[];
  surcharge: boolean;
}
export interface IEtaCalculatorOptions {
  baseMax: number;
  baseMin: number;
  deliveryMethod: string;
  productType: string;
  zip: string;
}

/** ORDER **/
export type ICpWarrantyStatus = 'paid' | 'active' | 'processing' | 'processed';
export type ICpWarrantySubstatus = 'pending' | 'active' | 'data_sent' | 'data_required' | 'data_completed';

export interface ICpReportReplacementArticle {
  amount: number;
  date: string | null;
  deliveredMethod?: string | null;
}
export interface ICpArticleReturnCaseExtraInformation {
  delayedAmount: number;
  delivered: ICpReportReplacementArticle;
  onTransit: ICpReportReplacementArticle;
}
export interface ICpArticleReturnCase {
  amount: number;
  id: number;
  status: string;
  article?: ICpArticleReturnCaseExtraInformation;
  dateToArchive?: string | null;
  lastUpdate?: string;
  number?: string;
  preferredSolution?: string;
  type?: string;
  createdAt: string;
}
export interface IArticleWarrantyInfo {
  id: string;
  isHardware: boolean;
  nationalVersion: boolean;
  brand:{
    title: string;
  }

}
export interface ICpArticleWarrantyReport {
  id: string;
  name: string;
  sku: string;
  picture: string;
  dne: string;
  link: string;
  quantity: number;
}

export interface IUsoCFDIs {
  id: string;
  name: string;
}
export interface ICpInvoiceData {
  accountDigits: string | null;
  email: string | null;
  id: string;
  isCreditPayment: boolean;
  orderNumber: string | null;
  orderText: string | null;
  organization: string | null;
  textId: string | null;
  texts: {
    default: boolean;
    display_checkout: number;
    id: string;
    title: string;
  }[];
  usageCode: string;
  userOrderNumber: string | null;
  usoCFDIs: IUsoCFDIs[] | null;
  ustId: string | null;
  cpTaxAddress?: string;
  taxRegimeCode?: string;
}
export interface IOrderInvoice {
  created: string;
  docdate: string;
  folio: string;
  id: string;
  minCreated: number;
  pdf: string;
  rawTotal: number;
  status: string;
  total: string;
  type: 'nota' | 'recibo' | 'factura';
  typeText: string;
  xml: string;
}

export interface ICpOrderStatus {
  failedSla: boolean;
  nr: number;
  substatus: string;
  text: string;
  tooltip: string;
  delayed: boolean;
}

export interface ISingleOrderReturnCase {
  amount: number;
  article: {
    delayedAmount: number;
    delivered: {
      amount: number;
      date: string | null;
    };
    onTransit: {
      amount: number;
      date: string | null;
      deliveredMethod: string | null;
    };
  } | null;
  articleImage: string;
  articleNumber: string;
  articleTitle: string;
  comment: string;
  dateToArchive: string;
  description: string;
  /** Return Case ID */
  id: string;
  /** Archive */
  isArchivable: boolean;
  isClosed: boolean;
  isWaybillRegenerable: boolean;
  /** Return Case Number */
  number: string;
  originalPackaging: boolean;
  /** Preferred Solution */
  preferredSolution: string;
  serialNumbers: string;
  /** Return Case Status */
  status: string;
  /** Return Case Type */
  type: string;
  lastUpdate?: string;
  createdAt: string;
  dueDate?: string;
}

export interface IArticleOrder {
  id: string;
  amount: number;
  amountUnconfirmed: number;
  articleId: string;
  canceled: boolean;
  etaRange: [number, number] | null;
  image: string;
  link: string;
  price: string;
  qty: string;
  qtyOriginal: string;
  returnCases: Array<Partial<ISingleOrderReturnCase>>;
  sku: string;
  title: string;
  type: string;
  isEsd: boolean;
  multiPlacement: boolean;
  allReviewsLink?: string;
  assembly?: number;
  warranty?: IArticleWarrantyInfo;
  // Prop used to avoid showing the return case actions when assembly order in on transit
  returnCaseAllowed?: boolean;
}
export interface IArticleWarrantyInfo {
  warranty?:{
    title: string;
    active: boolean;
    is_generic: boolean;
    is_countryVersionMex: boolean;
    type: 'Cyberpuerta' | 'Provider' | 'Brand';
    years: number;
    settings?: {
      folio_required: boolean;
    },
    contact_data?: {
      name: string;
      value: string;
      type: 'phone' | 'email' | 'url' | 'text';
    }[],
  },
}
export interface IOrder {
  addressBillComplete: string;
  addressBillRfc: string;
  addressDelComplete: string;
  addressDelName: string;
  addressDelPhone: string;
  addressId: string;
  allowEditInvoiceFields: boolean;
  archived: boolean;
  articles: Array<IArticleOrder>;
  conversionValue: number;
  countWorkdays: number;
  date: string;
  deliveredDate: string | null;
  deliveryMethod: string;
  deliverySet: string;
  editInvoiceFieldsDateRange: number;
  etaMax: string | null;
  etaMin: string | null;
  hasArticlesDelivered: boolean;
  hasCancelableProducts?: boolean;
  hasDelayedArticles: boolean;
  hasInvoices: boolean;
  hasReportsToArchive: boolean;
  hasReturnableProducts?: boolean;
  hasTracking: boolean;
  id: string;
  invoiceFields: ICpInvoiceData;
  invoices: IOrderInvoice[];
  isBaseUser: boolean;
  isCanceled: boolean;
  msi: string;
  naturalDaysSinceDelivered: number | null;
  naturalDaysSincePaid: number | null;
  notification: {
    regenerationWaybill: boolean;
  } | null;
  number: string;
  orderDateTimestamp: string | number;
  paidDateTimestamp: string;
  payment: {
    iva: number;
    netTotal: number;
    shipping: number;
    title: string;
    type: string;
    vouchers: Array<{
      discount: number; // voucher code
      number: string; // discount number
    }>;
    discounts?: [] | null;
    isCancelable?: boolean;
    pastDueDebt?: boolean;
    paydayLimit?: string;
    showCreditLimit?: boolean;
  };
  asssembliesStatus: Record<string, {
    text: string;
    tooltip: string;
    alert: boolean;
    ref: string;
    hasButton: boolean;
  }> | false;
  workingDaysSinceMaxETA: number;
  payment_name?: string;
  sentTo: string;
  status: ICpOrderStatus;
  total: string;
  userId: string;
  zipCode: string;
  garanPlus?: boolean;
  hasMultipleEtaDates: boolean;
  hasDelayedAndNeverSent?: boolean;
}

export interface IAccountPublicProfile {
  genre: string;
  id: string;
  levelUser: number;
  levelUserLabel: string;
  levelUserPicture: string;
  location: string;
  member_since: string;
  name: string;
  picture: string;
  profile: string;
  profile_value: string;
  ranking: number;
  ranking_data: {
    final_ranking_score: number;
    helpful_votes_multi: number;
    percent_helpful_multi: number;
    total_helpful_votes: number;
    total_review_multi: number;
    total_reviews: string;
  };
  salPic: string;
  total_answers: number;
  total_questions: number;
}

export type IUserRating = {
  helpPercent: string;
  id: string;
  picture: string;
  totalReviews: string;
  totalVotes: string;
  userAlias: string;
  userId: string;
  userLevel: string;
  userName: string;
  userRank: string;
};
export interface IBodyAction {
  title: string;
  icon: string;
  link?: string | ICpRouteLocationNamedRaw;
  partnerLogo?: boolean;
  target?: string;
}

export interface ITimeInterval {
  cptext: string;
  cpvalue: string;
  fk: string;
  total: number;
}

export type IPaginatedResponse<TData, TMeta extends Record<string, unknown> = Record<string, unknown>> = {
  data: TData[];
  links: {
    first: string;
    last: string;
    next: string | null;
    prev: string | null;
    self: string;
  };
  meta: TMeta & {
    itemsPerPage: number;
    page: {
      current: number;
      first: number;
      last: number;
      next: number;
      prev: number;
    };
    totalItems: number;
    totalPages: number;
    filters?: Array<{
      type: string;
      value: number;
    }>;
    timeIntervals?: Array<ITimeInterval>;
  };
};

export type StatusTypes =
  | 'invalid'
  | 'requested'
  | 'redeemed'
  | 'expired'
  | 'available'
  | 'activated'
  | 'archived'
  | 'archivedDefinedLifetime'
  | 'archivedForLife'
  | '';

export interface IESD {
  expirationDate: string;
  id: string;
  isExpired: boolean;
  link: string | null;
  orderNumber: string;
  productKey: string;
  status: StatusTypes;
  statusText: string;
  type: string;
}

export interface IDownload {
  articleId: string;
  esd: IESD;
  link: string;
  picture: string;
  sku: string;
  title: string;
}
export interface IDownloadsPagination {
  pageNumber?: number;
  pageSize?: number;
}

export type IDownloadPayload = IPaginatedResponse<IDownload>;
export interface IWishlistArticle {
  id: string;
  title: string;
  link: string;
  picture: string;
  sku: string;
  stock: number;
  firstBuyable: Array<{
    anid: string;
    aid: string;
    price: string;
    fprice: string;
  }>;
  price: string;
  priceValue: number;
  oldPrice: string;
  starsWidth: string;
  showStars: boolean;
  stockStatus: number;
  isDailyOffer: boolean;
  isOffer: boolean;
  offer: {
    date: string | false;
    percent: string | false;
    absolute: string | false;
    fabsolute: string;
    year: string | false;
    month: string | false;
    day: string | false;
  };
  isEsd: boolean;
  type: string;
  savedDate: string;
  savedPrice: number;
  articleId: string;
  eol: boolean;
  rating: {
    star1: number;
    star2: number;
    star3: number;
    star4: number;
    star5: number;
    global: number;
    total: number;
  };
  category: ICatMan;
  hideAddToCartButton: boolean;
}
export interface IWishlist {
  articles: Array<IWishlistArticle>;
  description: string;
  id: string;
  isDefault: boolean;
  title: string;
}

export interface ISignupFormData {
  confirmPassword: string;
  email: string;
  name: string;
  newsletter: boolean;
  password: string;
}

export interface ICpAddress {
  /** Estado */
  addressLevel1: string;
  /** Ciudad */
  addressLevel2: string;
  /** Colonia */
  addressLevel3: string;
  country: string;
  familyName: string;
  givenName: string;
  id: string;
  postalCode: string;
  sex: 'M' | 'F';
  streetName: string;
  streetNumber: string;
  tel: string;
  verified: boolean;
  additionalInfo?: string;
  interiorNumber?: string;
  organization?: string;
  telExtension?: string;
}
export interface IAccountAddresses {
  activeAddress: string;
  addresses: Array<ICpAddress>;
}
export type ValidationResult = string | boolean;
export type inputRules =
  | ValidationResult
  | PromiseLike<ValidationResult>
  | ((value: any) => ValidationResult)
  | ((value: any) => PromiseLike<ValidationResult>);

export type CpButtonIconKind =
  | 'chevron'
  | 'actions'
  | 'edit'
  | 'dropdown'
  | 'info'
  | 'question'
  | 'cancel'
  | 'delete'
  | 'close'
  | 'accordion'
  | 'add'
  | 'remove'
  | 'voted_on'
  | 'expand_less'
  | 'expand_more'
  | 'voted_off'
  | 'empty'
  | 'half'
  | 'full'
  | 'north_west'
  | 'arrow_back'
  | 'filter'
  | 'cart'
  | 'more_horiz'
  | 'send';

export type CpButtonIconType =
  | 'primary'
  | 'success'
  | 'warning'
  | 'secondary'
  | 'critical'
  | 'white'
  | 'neutral'
  | 'inCategory';

export interface IAccountSetting {
  description: string;
  id: string;
  selected: boolean;
  title: string;
  channel: string;
}

export interface IWarranty {
  id: string;
  sku: string;
  title: string;
  link: string;
  price: string;
  oldPrice: string;
  picture: string;
  starsWidth: string;
  showStars: boolean;
  stock: number;
  firstBuyable: unknown[]; // Replace 'any' with the appropriate type
  pictureMask?: string;
  warrantyDetail?: any[];
  dne?: string | null;
  freeShipping?: boolean;
  isDailyOffer?: boolean;
  isEsd?: boolean;
  isOffer?: boolean;
  offer?: {
    absolute?: string | boolean;
    date?: string | boolean;
    day?: number | boolean;
    fabsolute?: string | boolean;
    month?: number | boolean;
    percent?: string | boolean;
    year?: number | boolean;
  };
  priceValue?: number;
  rating?: {
    star1: number;
    star2: number;
    star3: number;
    star4: number;
    star5: number;
    global: number;
    total: number;
  };
  recommended?: boolean;
  stockStatus?: number;
  tPrice?: number;
  type?: string;
}
export type IWarrantyCard = {
  id: string;
  sku: string;
  title: string;
  link: string;
  price: string;
  oldPrice: string;
  picture: string;
  rate: number;
  showStars: boolean;
  totalReviews: number;
};
export interface ICpTextPricePair {
  price: number;
  text: string;
}
export type BlockType = 'ESD' | 'OCURRE' | string;

export type IPaymentMethodArticle = {
  id: string;
  link: string;
  picture: string;
};

export interface IPaymentMethodActionButton {
  articles: IPaymentMethodArticle[];
  blockType: BlockType;
  forzoso: boolean;
  label: string;
  labelBtn: string;
  labelBtnExtraText: string;
  labelHtml: string;
  link: string;
}

export interface IEtaData {
  etaRange: [number, number];
  soc: 'MEX' | 'USA';
  type: string;
  zip: string;
  delId?: string;
}

export interface ICpBasketArticle {
  aid: string;
  amount: number;
  basketItemId: string;
  brand: ICatMan;
  category: ICatMan;
  dne: string;
  eol: boolean;
  etaData: IEtaData;
  firstBuyable: {aid: string; anid: string};
  id: string;
  isEsd: boolean;
  link: string;
  nettoPrice: number;
  nettoTotal: number;
  bruttoPrice: number;
  bruttoTotal: number;
  offer: {
    absolute?: number;
    date?: string;
    day?: number;
    fabsolute?: string;
    month?: number;
    percent?: number;
    year?: number;
  };
  oldPrice: number;
  picture: string;
  price: number;
  serialNumber: string | null;
  isGift: boolean;
  assembly: boolean | null;
  savedDate: string;
  shippingOriginCountry: 'MEX' | 'USA';
  sku: string;
  stock: number;
  stockStatus: number;
  title: string;
  total: number;
  unlimitedSupply: boolean;
  varSelect: string; // Warehouse
  isDailyOffer?: boolean;
  isCountryVersionMex?: boolean;
  nationalVersion?: string;
  warranty?: IArticleWarrantySupplier;
}
export interface ICpBasketSummary {
  bruttoDeliveryCost: number | null;
  deliveryCost: number;
  discounts: ICpTextPricePair[];
  paymentCost: number;
  saldoData: {
    active: boolean;
    amount: number;
    incompleteAmount: boolean;
    saldoNeeded: number;
  };
  total1: number | null;
  total2: number | null;
  vouchers: ICpTextPricePair[];
}

export interface ICpBasketMessage {
  dataLayerType: string;
  message: string;
  messageType: string;
  image?: string;
  images?: string[];
  prices?: string[];
  sku?: string;
  oldPrice?: string;
  currentPrice?: string;
}

export interface IEtaGroupProduct {
  id: string;
  link: string;
  picture: string;
  title: string;
  type: string;
}
export interface IEtaGroup {
  etaRawData: {
    etaRange?: [number?, number?];
    soc: 'MEX' | 'USA';
    type: string;
    zip: string;
  };
  products: Array<IEtaGroupProduct>;
}

export interface IDeliverySet {
  deactivate: boolean;
  description: string;
  id: string;
  image: string;
  price: number;
  selected: boolean;
  shipSetGroupId: string;
  slaRate: number;
  title: string;
  denied?: boolean;
}

export interface IDeliverySetGroup {
  deliverySets: IDeliverySet[];
  etaGroups: Array<IEtaGroup>;
}

export interface ICPBasketPaymentMethods {
  actionButton: IPaymentMethodActionButton;
  active: boolean;
  id: string;
  addressVerificationRequired?: 'none' | 'real-time-payment' | 'saf'
}

export interface IMSIData {
  msi?: {installment?: string; min_total?: string; month?: number};
  select?: {installment?: string; min_total?: string; month?: number};
  type?: number;
}
export interface ICpBasketContents {
  basket: {
    articles: Partial<ICpBasketArticle>[];
    deliveryMethod: string;
    msiData: IMSIData | [];
    paymentMethod: string | null;
    numberOfItems?: number;
  };
  deliverySetGroups: Array<IDeliverySetGroup>;
  errors: {errors?: Array<string>; id?: string};
  extraCosts: ICpBasketSummary;
  paymentMethods: Array<ICPBasketPaymentMethods>;
  subtotal: number;
  total: number;
  validationMessages: ICpBasketMessage[];
}

export interface IBasketMessage {
  dataLayerType: string;
  currentPrice: string;
  message: string;
  messageType: string;
  image?: string;
  oldPrice: string;
  sku?: string;
  title: string;
}

export type IBasketAccessories = Array<{
  articles: ICpBasketArticle[];
  category: ICatMan;
}>;

export interface ILicence {
  count: number;
  articleId: string;
  picture: string;
  link: string;
  title: string;
  sku: string;
  esd: {
    orderNumber: string;
    id: string;
    isExpired: boolean;
    date: string;
    status: string;
    statusText: string;
    productKey: string;
    link: string;
    type: string;
    total: string;
  };
}

export interface IBillingFormData {
  cpTaxAddress: string;
  invoiceUsageCode: string;
  organization: string;
  rfc: string;
  taxRegimeCode: string;
}

export interface ITaxRegimesData {
  id: string;
  isForLegalPerson: boolean;
  isForNaturalPerson: boolean;
  title: string;
}

export interface IUsageCodesData {
  id: string;
  isForLegalPerson: boolean;
  isForNaturalPerson: boolean;
  title: string;
}

export interface InvoiceUsageCode {
  id: string;
  isForLegalPerson: boolean;
  isForNaturalPerson: boolean;
  title: string;
}
export interface ICpCardMyWarrantiesDataStatusProps {
  number: number;
  status: string;
  serialNumber: string;
  name: string;
  email: string;
  businessName: string;
  address: string;
  phone: string;
}

export interface IAccount {
  isLoggedIn: boolean;
  isBaseLogin: null | boolean;
  picture: string;
  name: string;
  nameAccount: string;
  accountUrls: Array<{
    url: string;
    lang: string;
  }>;
  defaultPicture: string;
  email: string;
  id: string;
  saldo: {
    active: boolean;
    amount: number;
    minimumOrderValue: number;
    appliedSaldo: [];
  };
  clientReference: null | string;
  anonId: string;
  zip: null | string;
  emailActivation: boolean;
  salesman: null;
  isProcurement: null | boolean;
  extraData: {
    autoLoginStatus: number;
    noPassword: boolean;
    fidProviders: Array<string>;
  };
  identityVerificationStatus: string;
}

export interface ICardReport {
  counter: number;
  productName: string;
  sku: string;
  picture: string;
  kind: 'standard' | 'cancelled' | 'returnCase' | 'overdue' | 'returnCaseUpdated';
  link: string;
}
export interface ICardOrder {
  counter: number;
  productName: string;
  sku: string;
  picture: string;
  kind: 'standard' | 'cancelled' | 'oos' | 'overdue';
}

export interface IProductCardReportQuantity {
  quantity: number;
  product: ICardOrder;
}

export interface ICardActivation {
  productName: string;
  sku: string;
  picture: string;
  warranty: boolean;
  activationSku: boolean;
}
export interface IBillingDataItem {
  email: string;
  id: string;
  invoiceUsageCode: string;
  organization: string;
  rfc: string;
  cpTaxAddress: string;
  taxRegimeCode: string;
  taxRegimeOptions?: any[];
}

export type IBillingDataItemUpdate = Omit<IBillingDataItem, 'id'> & {
  autoSelect?: boolean;
};

export interface IBillingData {
  activeBillingData: string | null;
  billingData: Array<IBillingDataItem>;
}

export interface IPaypalData {
  id: string;
  state: 'ACTIVE' | 'INACTIVE'; // just those two options?
  payer: any;
  create_time: string;
  update_time: string;
  canceled_time?: string;
  description: string;
  merchant: any;
  plan: any;
  links: any[];
}

export interface IPaypalInstallment {
    month: number,
    value: number,
    msi: 'RESTRICTED' | 'YES' | 'NO',
    total: number,
    minOrderTotal: number,
    passCostToCustomer: number,
    financingSurchargeAbs: number,
    financingSurchargePer: number,
    totalWithSurcharge: number,
    cat: number
}

export interface IPdp_wishlist {
  productId: string;
  fromPdp: boolean;
  originListId?: string;
  isDeleting?: boolean;
}
export interface ITrackingCodeTracked {
  codecount: number;
  // eslint-disable-next-line no-use-before-define
  codes: ITrackingCodes[];
  delivery: string;
  id: string;
  // eslint-disable-next-line no-use-before-define
  products: ITrackingCodeProduct[];
  statuses: string;
}

export interface ITrackingCodeProduct {
  amount: string | number;
  id: string;
  image: string;
  isEsd: boolean;
  link: string;
  name: string;
  failed_shop_eta: boolean;
  on_transit_9_days: boolean;
  max_eta_date: string;
  max_eta_date_unix: number;
  type: string;
  sku: string;
}

export interface ITrackingCodeHistory {
  acronym: string;
  city: string;
  code: string;
  codedesc: string;
  datetime: string;
  desc: string;
  id: string;
}

export interface ITrackingCodes {
  code: string;
  detail: {
    from: {
      city: string | null;
      datetime: string;
    };
    history: ITrackingCodeHistory[];
    last_update: string | null;
    logisticProvider: string | null;
    reload: boolean;
    showMore: boolean;
    status: string;
    to: {
      city: string | null;
      datetime: string;
      receptor: string | null;
      zip: string | null;
    };
  };
}

export interface ITrackingCode {
  hasPending: number;
  hasTracked: number;
  orderNumber: string;
  pending: Array<
    {
      codecount: number;
      codes: ITrackingCodes[];
      delivery: string;
      id: string;
      products: ITrackingCodeProduct[];
      statuses: string;
    }
  >;
  tracked: ITrackingCodeTracked[];
}

export interface ICalc {
  low: {
    display: boolean;
    bracket: number;
  };
  line: {
    [month: string]: {
      cphigh_bracket_min_order_total: number;
      cphigh_bracket_pass_cost_to_customer: number;
      'Installments ($)': number;
      'Batch: "Meses sin intereses"': 'YES' | 'RESTRICTED' | 'NO';
      'Updated order total (incl. financing surcharge) ($)': number;
      'Financing surcharge (%)': string;
      'Costo annual total CAT (%)': string;
    };
  };
}
export interface IRememberedCard {
    id: string,
    status: string,
    source: {
        card: {
            brand: string,
            last_digits: string,
            verification_status: string
        }
    },
    links?: Array<{
        href: string,
        rel: string,
        method: string,
        encType: string
    }>
}
export interface IPaymentInstallmentsData {
    display: boolean;
    total: number;
    lowerBracket: number;
    preferedMonth: number;
    installments: IPaypalInstallment[]
}
export interface IPaymentMethod {
    id: string,
    title: string,
    description: {
        text: string,
        bullets: string[],
    },
    images: string[]
}

export interface IInvoiceDetails {
  status: 'Vigente' | 'Cancelada' | 'En proceso de cancelación';
  invoice: string;
  created: string;
  typeText: string;
  certificationDate: string;
  total: string;
}

export interface ISizes {
  width: number;
  height: number;
}

export interface ISignature {
  format: string;
  signature: number[];
  description: string;
  image?: string;
}

export interface IFileUpload {
  fieldName: string;
  element: File;
  name: string;
  size: number;
  type: string;
  src: string;
  width: number;
  height: number;
  valid: boolean;
  mime: string;
}
export interface IWarrantyStatus {
  status: string;
  text: string;
  tooltip: string;
}

export interface IPaymentInfo {
  paymentMethod: string;
  invoiceUsageCode: string;
  lastDigits: string;
  order: string;
  message: string;
}

export interface IProductCard {
  kind: 'FeaturedOffer' | 'Standard' | 'Offer' | 'Featured' | 'Oos' | 'OosOffer';
  productType: 'physical' | 'esd';
  pillLarge: boolean;
  pillShort: boolean;
  rate: boolean;
  rateValue: number;
  reviews: number;
  freeShipping: boolean;
  link: string;
  title: string;
  hasDiscount: boolean;
  discount: string;
  picture: string;
  imageTitle: string;
  price: number;
  tPrice: number;
  id: string;
}

export interface IReviews {
  article_id: string;
  body: string;
  id: string;
  source: 'cp';
  negative_votes: number;
  origin: string | null;
  positive_votes: number;
  rating: number;
  status: string;
  title: string;
  user_id: string;
  user_name: string;
  user_pic: string | null;
  status_reason: string;
  company_industry: string;
  company_size: string;
  brand_response: any;
  brand_author: any;
  brand_date: any;
  brand_edit: number;
  variant_name: string | null;
  variant_sku: string;
  created_at: string;
  verified_purchase: boolean | number;
  votes: number;
}

export interface IProductReview extends IReviews {
  articleId: string;
}

export interface IAccountReview extends IReviews {
  article: ICpArticle;
}

export type ansQuestion = {
  body: string;
  id: string;
};

export interface IAnswer {
  articleId: string;
  body: string;
  date: string;
  id: string;
  negativeVotes: string;
  positiveVotes: string;
  question: ansQuestion;
  totalVotes: number;
}

export interface IDetailsAnswer {
  attachments: {
    image: Array<string>;
    urls: Array<string>;
    video: Array<string>;
  };
  body: string;
  date: string;
  flagBrand: string;
  id: string;
  isBestAnswer: boolean;
  negativeVotes: string;
  positiveVotes: string;
  status: string;
  timestamp: number;
  userId: string;
  userName: string;
  userPic: string;
  verifiedPurchase: boolean;
  wasEdited: boolean;
}

export interface IDetailsQuestion {
  answerCount: number;
  answers: Array<IDetailsAnswer>;
  articleId: string;
  body: string;
  date: string;
  id: string;
  moreAnswers: boolean;
  sendNotification: number;
  status: string;
  timestamp: number;
  totalAnswers: number;
  userId: string;
  userPic: string;
  username: string;
}

export type IMyAnswerProps = {
  answer: IAnswer;
  article: ICpArticle;
  question: IDetailsQuestion;
};

export interface ICpsp {
  /** to filter by Category */
  cat?: string[];
  /** to filter by Attributes, also to filter by free shipping */
  cklist?: {[attrId: string]: string[]};
  /** to filter by being free shipping */
  envio?: string;
  /** to filter by Having more than 0 stock */
  instock?: string;
  /** to filter by New articles, i.e. newer than 2 weeks */
  isnew?: string;
  /** to filter by Having some promo, such as discount */
  isoffer?: string;
  /** to filter by Manufacturers */
  man?: string[];
  /** to filter specific offers */
  msku?: string;
  /** to filter by Maximum Price */
  pricemax?: number;
  /** to filter by Minimum Price */
  pricemin?: number;
  /** to filter by Stars, Rating, Ranking */
  ranking?: number[];
  /** to filter by Search term */
  search?: string;
  /** to filter by Shipping Origin Country */
  soc?: string[];
}
type CategoryListingResponse = {
  cl: 'alist';
  cnid: string;
  cpsp?: ICpsp;
  msku?: string;
  listorderby?: string;
  listorder?: string;
};

type BrandListingResponse = {
  cl: 'manufacturerlist';
  mnid: string;
  cpsp?: ICpsp;
  msku?: string;
  listorderby?: string;
  listorder?: string;
};

type SearchListingResponse = {
  cl: 'search';
  searchparam: string;
  cpsp?: ICpsp;
  msku?: string;
  listorderby?: string;
  listorder?: string;
};

export type ProductDetailResponse = {
  anid: string;
  cl: 'details';
  cnid?: string;
};

type ForgotPasswordResponse = {
  cl: 'forgotpwd';
  email: string;
  uid: string;
};

type UserAccountResponse = {
  cl: 'account';
};

type BasketResponse = {
  cl: 'basket';
};

type WishlistResponse = {
  cl: 'emwishlist_accountwishlists';
};

type TrackOrderResponse = {
  cl: 'account_order';
  oxorderid: string;
};

type MyReviewsResponse = {
  cl: 'cpaccount_reviews';
};

type DeliveryAddressResponse = {
  cl: 'account_user';
};

type MyQuestionsResponse = {
  cl: 'cpqa_myquestions';
};

export type CmsPagesResponse = {
  cl: 'content';
  oxloadid: string;
  oxcid: string;
  lang: string;
};

type WriteQuestionResponse = {
  cl: 'cpqa_writequestion';
};

type WriteReview = {
  cl: 'cpreviews_write';
  anid: string;
};

type RegisterResponse = {
  cl: 'register';
};

type CMSResponse = {
  cl: 'content';
};

export type ParseUrlCatalogResponse = CategoryListingResponse | BrandListingResponse | SearchListingResponse;

export type ParseUrlResponse =
  | ParseUrlCatalogResponse
  | ProductDetailResponse
  | ForgotPasswordResponse
  | UserAccountResponse
  | BasketResponse
  | WishlistResponse
  | TrackOrderResponse
  | MyReviewsResponse
  | MyQuestionsResponse
  | DeliveryAddressResponse
  | CmsPagesResponse
  | RegisterResponse
  | WriteQuestionResponse
  | CMSResponse
  | WriteReview;

export type MenuSelected = 'home' | 'categories' | 'account' | 'menu' | null;

export interface IHeaderOptions {
  title?: string;
  headline?: string;
  numOfItems?: number;
  showSearch?: boolean;
  placeholder?: string;
  showCart?: boolean;
  showGoBack?: boolean;
  showAccount?: boolean;
  blueVersion?: boolean;
  showLongSearchBar?: boolean;
  searchFocus?: boolean;
  isFakeTitle?: boolean;
}

export interface IMetaDataRoute {
  title?: string;
  name?: string;
  menuSelected: MenuSelected;
  navHeaderOptions?: IHeaderOptions;
  navHeaderOptionsAuth?: IHeaderOptions;
  path?: string;
  titleTemplate?: (title?: string) => string | null;
}

export interface IBanner {
  name: string;
  order: number;
  pictureUrl: string;
  targetUrl: string | null;
  tier: number;
}
export interface IBannerPlace {
  banners: Array<IBanner>;
  place: string;
  placeType: 'slider' | 'static' | 'stackable';
}
export interface IBannerContext {
  /** e.g. id */
  name: string;
  /** e.g. the cnid of the current page */
  value: string | string[];
}

export type ISpecialLink = {
  id: string;
  text: string;
  image: string;
  title: string;
  url: string;
};

export interface IFilterElement {
  description: string;
  hide: boolean;
  selected: boolean;
  title: string;
  tpl: string;
  h?: string;
}

export interface IPFCheckBox {
  canHide: boolean;
  count: number;
  hash: string; // md5 of value I think... used to get the count of each checkbox
  name: keyof ICpsp;
  selected: boolean; // overwritten sometimes!
  seoName: string;
  seoValue: string;
  title: string;
  value: string;
}
export interface IElementAttributes extends IFilterElement {
  checkBoxes: Array<IPFCheckBox>;
  id: string;
  name?: string;
  labelClass?: string;
  max?: {current: number; price: number};
  min?: {current: number; price: number};
  preferredValues?: Array<string>;
  rootCategories?: Record<string, Omit<IElementAttributes, 'rootCategories'>>;
  showToggleButton?: boolean;
}
export interface ICatalogFilter {
  actContentId: string;
  actTpl: string;
  actionLink: string;
  attributes: Array<IElementAttributes>;
  baseLink: string;
  cl: 'manufacturerlist' | 'search' | 'alist';
  currentLocation: string;
  deleteLabelGroups: Array<{
    labels: Array<{link: string; title: string}>;
    title: string;
  }>;
  filterElements: Array<IElementAttributes>;
  isFilterSet: boolean;
  moreAttributes: boolean;
  navParams: string;
  usefulPaths: Array<{link: string; title: string}>;
  search?: {show: boolean; term: string};
}
export type FilterGroup = {
  group: string;
  id: string;
  name: string;
  parentName: string;
  value: string;
};
export type FiltersMap = {
  categories: {[key: string]: FilterGroup};
  cklist: {[key: string]: FilterGroup[]};
  man: {[key: string]: FilterGroup};
  stars: {[key: string]: FilterGroup[]};
  statuses: {[key: string]: FilterGroup};
};
export interface ISeoProps {
  canonical: string;
  canonicalTag: string;
  longDesc: string;
  metaDescription: string;
  metaRobots: string;
  metaTags: string;
  pageTitle: string;
  siteTitle: string;
  image: string;
}
export type CatalogType = 'cat' | 'man' | 'search';
export interface ICatalog {
  bannerContext: Array<{name: string; value: string[]}>;
  featuredCategories: ISpecialLink[];
  filters: ICatalogFilter;
  /** This is an extra type (API doesn't return it), useful to handle filters in the Frontend */
  filtersMap: FiltersMap;
  id: string;
  isParent: boolean;
  redirectUrl: string;
  seoProps: ISeoProps;
  specialLinks: ISpecialLink[];
  title: string;
  type: CatalogType;
  autocomplete: string;
}

export interface FilterStatus {
  instock: number;
  envio: number;
  isoffer: number;
  isnew: number;
}
// Temp Interface IFilters
export interface IFilters {
  cpsp: [];
  autocomplete: string;
  counts: {
    man: any;
    cklist: any;
    status: FilterStatus;
    cat: any;
    soc: {
      MEX: number;
    };
    stars: {
      0: number;
      1: number;
      2: number;
      3: number;
      4: number;
      5: number;
    };
    price: {
      0: number;
      1: number;
    };
  };
  price: number[];
  stars: number[];
  articleIds: string[];
  total: number;
  page: number;
  urls: string[];
  seoProps: {
    canonicalTag: string;
    longDesc: any;
    metaDescription: any;
    metaRobots: any;
    metaTags: any;
    pageTitle: string;
    siteTitle: string;
    image: string;
  };
}
export interface IOrdersStore {
  currentSearch: string;
  defaultTimeInterval: number | 'archived' | undefined;
  timeInterval: number | 'archived' | undefined;
  totalOrders: number | undefined;
  selectedOrderId?: string;
  createdOrder: {
    number: string;
    realTimePayment: boolean;
  };
  orderTrackerId?: string;
  orderData: IOrder | null;
  selectedOrderArticle: IArticleOrder | null;
}
export interface ICpspStore {
  /** to filter by Category */
  'cpsp[cat]'?: string[];
  /** to filter by Attributes, also to filter by free shipping */
  'cpsp[cklist]'?: {[attrId: string]: string[]};
  /** to filter by being free shipping */
  'cpsp[envio]'?: string;
  /** to filter by Having more than 0 stock */
  'cpsp[instock]'?: string;
  /** to filter by New articles, i.e. newer than 2 weeks */
  'cpsp[isnew]'?: string;
  /** to filter by saldo */
  'cpsp[saldo]'?: string;
  /** to filter by Having some promo, such as discount */
  'cpsp[isoffer]'?: string;
  /** to filter by Manufacturers */
  'cpsp[man]'?: string[];
  /** to filter by Maximum Price */
  'cpsp[pricemax]'?: number;
  /** to filter by Minimum Price */
  'cpsp[pricemin]'?: number;
  /** to filter by Stars, Rating, Ranking */
  'cpsp[ranking]'?: number[];
  /** to filter by Search term */
  'cpsp[search]'?: string;
  /** to filter by Shipping Origin Country */
  'cpsp[soc]'?: string[];
  /** to filter by Shipping Origin Country */
  sort?: string;
}

export interface ICpspAppliedFilter {
  status?: ICpspStore;
  cklist: any;
  opinion?: ICpspStore;
  price?: ICpspStore;
  cat?: string[];
  search?: string;
}

export type ISearchSuggestions = {
  articleIds: Array<string>;
  autocomplete: Array<string>;
  brands: Array<ICatMan>;
  categories: Array<ICatMan>;
  url: string;
};

export interface IFidAccountData {
  fids: Array<{
    id: number;
    provider: string;
    username: string;
  }>;
}

export interface IDropdownTwoLevelsModelFilterDataSubItem {
  title: string;
  value: string;
  count: number;
  canHide: boolean;
}

export interface IDropdownTwoLevelsModelFilterDataItem {
  title: string;
  key: string;
  maxItems: number;
  subItems: IDropdownTwoLevelsModelFilterDataSubItem[];
}

export interface IDropdownTwoLevelsModelFilterData {
  title: string;
  items: IDropdownTwoLevelsModelFilterDataItem[];
}

export type IDropdownTwoLevelsValue = Record<string, string[]>

export interface IMyWarrantyInfo {
  warranty_id: string;
  status: 'pending' | 'incomplete' | 'completed' | 'data_sent';
  cpfon: string;
  cpzip: string;
  cpcity: string;
  cpemail: string;
  cpfname: string;
  cplname: string;
  cpstate: string;
  cpstreet: string;
  cpcolonia: string;
  cpcompany: string;
  cpstreetnr: string;
  cpstreetnrint: string;
  cpserial_number: string;
  cpparent_product?: string; // For HP warranties
  cpparent_title?: string; // For HP warranties
}

export interface IMyWarrantyArticle {
  id: string;
  order_article_id: string;
  title: string;
  sku: string;
  amount: number;
  image: string;
  link: string;
  warranty_info: Array<IMyWarrantyInfo>;
}

export interface IMyWarranty {
  brand: string;
  status: 'paid' | 'active' | 'processing' | 'processed';
  sub_status: 'pending' | 'active' | 'data_sent' | 'data_required' | 'data_completed';
  message: string;
  serial_num_flow: boolean;
  total: number;
  articles: Array<IMyWarrantyArticle>;
}

export interface IMyWarrantyOrder {
  type: string;
  id: string;
  attributes: {
    order_number: string;
    date: string;
    warranties: Array<IMyWarranty>;
  };
}

export interface ISingleMyWarrantyOrder {
  data: {
    type: string;
    attributes: IMyWarranty;
  };
}

export interface IWarrantyContactData {
  name: string,
  type: string,
  value: string,
}[];

export interface IArticleWarrantySupplier {
  title: string,
  active: boolean,
  is_generic: boolean,
  isCountryVersionMex: boolean,
  type: 'manufacturer' | 'provider' | 'cyberpuerta',
  years: number,
  settings: {
    needs_folio: boolean,
    inverted_doa: boolean
  } | null,
  contact_data: IWarrantyContactData[] | null,
  nationalVersion: string | null,
}

export interface ICarePackProduct {
  sku: string;
  article: {
    id: string;
    title: string;
    link: string;
    picture: string;
    sku: string;
    stock: number;
    firstBuyable: Array<{
      anid: string;
      aid: string;
      price: number;
      fprice: string;
    }>;
    price: string;
    oldPrice: string;
    starsWidth: string;
    showStars: boolean;
    stockStatus: number;
    isDailyOffer: boolean;
    isOffer: boolean;
    offer: {
      date: string | false;
      percent: string | false;
      absolute: string | false;
      fabsolute: string;
      year: string | false;
      month: string | false;
      day: string | false;
    };
    isEsd: boolean;
    priceValue: number;
    tPrice: number;
    dne: string | null;
    hideAddToCartButton: boolean;
    type: string;
    freeShipping: boolean;
    rating: Record<`star${'1' | '2' | '3' | '4' | '5'}`, number> & {
      global: number;
      total: number;
    };
  };
  warrantyStartDate: string;
  warrantyEndDate: string;
}

export interface ISingleOrderReturnCaseArticle {
  /** Use this as limit for counters when generating report */
  amount: number;
  amountConfirmed: number;
  amountOnTransit: number;
  amountReceived: number;
  articleId: string;
  articleNumber: string;
  daysSinceOnTransit: number;
  daysSinceReceived: number;
  image: string;
  isEsd: boolean;
  isReturnable: boolean;
  /** Ask for serial number if true */
  multiPlacement: boolean;
  price: number;
  returnCases: number;
  returnCasesOnTransit: number;
  title: string;
  deliveredDate: string;
  allDelivered?: boolean;
  warranty?:IArticleWarrantyInfo;
}

export type IReportType = 'orderDefect' | 'productError' | 'regularReturn' | 'productNotDelivered';
export type IReportSubtype =
  | 'manufacturerDefect' // ask for serial number for this type
  | 'shippingDefect'
  | 'modelError'
  | 'specError';
export type IWarrantyKind = 'Cp' | 'Brand' | 'FolioRequired' | 'Provider';

export interface IReportSubTypes {
  allowClosedPackages: boolean;
  allowOpenPackages: boolean;
  allowRefund: boolean;
  allowReplacement: boolean;
  limit: string | undefined | null;
  requirePackageStatus: boolean;
  requirePictures: boolean;
  requirePreferredSolution: boolean;
  requireSerialNumbers: boolean;
  text: string;
  type: IReportSubtype;
}
export interface IReportTypes {
  forDeliveredArticles: boolean;
  limit: string | undefined | null;
  subTypes: IReportSubTypes[] | undefined | null;
  text: string;
  type: IReportType;
  allowClosedPackages?: boolean;
  allowOpenPackages?: boolean;
  allowRefund?: boolean;
  allowReplacement?: boolean;
  requirePackageStatus?: boolean;
  requirePictures?: boolean;
  requirePreferredSolution?: boolean;
  requireSerialNumbers?: boolean;
}
export interface ISingleOrderReturnCases {
  articles: ISingleOrderReturnCaseArticle[];
  /** Used when you want to cancel order */
  delayedArticles: ISingleOrderReturnCaseArticle[];
  /** User has not received the product */
  hasCancelableProducts: boolean;
  /** User has received products but it has some defects */
  hasReportsToArchive: boolean;
  /** Has returnable products - Generate Report */
  hasReturnableProducts: boolean;
  isCredit: boolean;
  naturalDaysSincePaid: number;
  orderArticleActive: string;
  orderDate: string;
  orderId: string;
  orderNumber: string;
  paymentType: string;
  /** Tells if user needs to regenerate waybill */
  regenerateWaybill: boolean | undefined;
  /** Depends on hasReturnableProducts */
  reportableArticlesOnTransit: ISingleOrderReturnCaseArticle[];
  /** Depends on hasReturnableProducts */
  reportableDeliveredArticles: ISingleOrderReturnCaseArticle[];
  /** Disabled when generating report */
  reportedArticles: ISingleOrderReturnCaseArticle[];
  returnCaseActive: ISingleOrderReturnCase | null;
  returnCases: ISingleOrderReturnCase[] | null;
  returnModuleSettings: {
    daysToArchive: {
      general: number;
      productNotDelivered: number;
      shippingDefect: number;
    };
    reportTypes: IReportTypes[];
  };
  /** Disabled when generating report */
  unavailableArticles: ISingleOrderReturnCaseArticle[];
  /** Tells if a report needs update */
  updateReport: boolean;
  userId: string;
}

export interface IPaymentBlock {
  widgets: Array<{
    type: 'alert' | 'text' | 'grid' | 'divider' | 'image';
    variant: string;
    content:
      | string
      | Array<
          Array<{
            type: string;
            variant: 'body-regular' | 'body-bold' | 'subheading';
            content: string;
          }>
        >;
  }>;
}

export interface IRadioButtonItem<T> {
  label: string;
  value: T;
  quantity?: number;
}

export enum SortReviewsOptions {
  'SORT_BY_POSITIVE_VOTES' = 1,
  'SORT_BY_MOST_RECENT' = 2,
  'SORT_BY_OLDEST_REVIEW' = 3,
  'SORT_BY_BEST_RATING' = 4,
  'SORT_BY_WORST_RATING' = 5,
}

export interface IReviewsFiltersRadioButtonItem extends IRadioButtonItem<SortReviewsOptions> {
  value: SortReviewsOptions;
}

export interface IReviewsOptions {
  sortType: SortReviewsOptions;
  search: string;
  'page[number]': number;
}

export interface IAppliedFilter {
  label: string;
  value: string | any;
}

export interface IAppliedFilterType {
  value: string;
  label: string;
  appliedFilters: IAppliedFilter[];
}

export interface IWaybillGroup {
  status: string;
  waybills: Array<{
    number: string;
    status: string;
  }>;
  articles: Array<{
    orderArtId: string;
    sku: string;
    amount: string;
    maxEtaDate: string;
  }>;
}

export interface IArticlesPerWaybill {
  data: {
    groups: Array<IWaybillGroup>;
  };
}

// Visit IT-21815 for more context
export interface ImsiInstallments {
  month: number;
  value: number;
  msi: 'RESTRICTED' | 'YES' | 'NO';
  minOrderTotal: number;
  totalWithSurcharge: number;
  financingSurchargeAbs: number;
  financingSurchargePer: number;
  passCostToCustomer: number;
  cat: number;
}

export interface ImsiInfo {
  type: number;
  /* NOTE: type possible responses
        0 - msi off, not enough to offer monthly payments
        1 - <[minimium price] access monthly payments but not MSI
        2 - offer min msi- access monthly payments and we offer the first msi achievable
        3 - msi reached - the user has reached all the possible msi options, we can't offer more
        4 - offer next msi - We already have msi and the user can unlock more options
    */
  title: null | {
    month: number;
    value: number;
    diff?: number;
  };
  text: null | {
    month?: number;
    value: number;
    diff?: number;
  };
  tooltip: null | {
    month?: number;
    value: number;
    diff?: number;
  };
}
export interface IPaypalInstallmentsMSI {
    data: {
        installments: Array<ImsiInstallments>,
        msi: ImsiInfo
    }
}

export interface ICpmUpgradesPayload {
  component_id: string;
  id: string;
  price: number;
  value: string;
}

export interface ISpecialLinkCard {
  id: string;
  title: string;
  text: string;
  image: string;
  url: string;
  selected?: boolean;
}

export interface IStoreRatings {
  appStore: {numberOfReviews: number; rating: string};
  playStore: {numberOfReviews: number; rating: string};
}

// Payment block temporal solution IT-24649
interface IPaymentInstructionsTaxRegime {
  id: string | null;
  title: string | null;
  isForNaturalPerson: boolean;
  isForLegalPerson: boolean;
}

interface IPaymentInstructionsUsageCode {
  id: string | null;
  title: string | null;
  isForNaturalPerson: boolean;
  isForLegalPerson: boolean;
}

interface IPaymentInstructionsAddress {
  address_line_1: string;
  address_line_2: string;
  zip_code: string;
  city: string;
  state: string;
  phone: string;
  additional_info: string;
}

interface IPaymentInstructionsBillingData {
  rfc: string | null;
  company: string | null;
  zip_code: string | null;
  tax_regime: IPaymentInstructionsTaxRegime | null;
  usage_code: IPaymentInstructionsUsageCode | null;
}

export interface IPaymentInstructions {
  data: {
    id: string;
    number: string;
    payment_type: string;
    total: number;
    refClient: number | null;
    convenio_cie: string | null;
    clabe_bbva: string | null;
    clabe_monex: string | null;
    oxxo_reference: string | null;
    oxxo_barcode_url: string | null;
    billing_data: IPaymentInstructionsBillingData;
    address: IPaymentInstructionsAddress;
    articles: IArticleOrder[];
    delivery_method: string;
    showCybermerch: boolean;
    payment_description: string;
  }
}

export interface ICheckboxItem<T> {
  label: string
  value: T
  canHide: boolean
  quantity?: number
}

export type RangeType = { max?: number, min?: number }

export enum EComponents {
  PROCESSOR = 'Procesador',
  RAM = 'Memoria Ram',
  MAIN_STORAGE = 'Almacenamiento Principal',
  ADDITIONAL_STORAGE = 'Almacenamiento Adicional',
  EXTENDED_STORAGE = 'Almacenamiento Extendido',
  COMPLEMENTARY_STORAGE = 'Almacenamiento Complementario',
  VIDEO_CARD = 'Tarjeta de video',
}
export enum EAttributes {
  PROCESSOR_FAMILY = 'Familia de procesador',
  PROCESSOR_MODEL = 'Modelo del procesador',
  RAM = 'Memoria RAM',
  GRAPHIC_PROCESSOR = 'Procesador gráfico',
  STORAGE = 'Capacidad',
}

interface IDeliverySetItemDescription {
  basketText: string;
  paymentText: string;
  orderText: string;
}

interface IDeliverySetItemData {
  id: string;
  title: string;
  description: IDeliverySetItemDescription;
  images: string[];
  maxWeightProduct: string;
  shippingDuration: string[];
  shippingDurationOuterZone: string[];
  rate: string | number;
}

export type IDeliverySetData = IDeliverySetItemData[];
